import { PlusOutlined } from "@ant-design/icons";
import { Button, Col, Divider, Flex, Select } from "antd";
import React from "react";
import { NoteType } from "../../domain/notes.domain";
import { PRACTICE_AREA } from "../../domain/user.domain";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { useIsMobileView } from "../../hooks/ui-hook";
import { useFuseSearch } from "../../hooks/useFuseSearch";
import { getCustomNoteList } from "../../service/notes.service";
import { getTemplates } from "../../service/templates.service";
import {
  setAITemplateModalState,
  setAllTemplates,
  setShowRestrictionModal,
  setTemplateOpenedFromNoteModal,
} from "../../slices/appStateSlice";
import { setRecordingDetail } from "../../slices/recordingsSlice";
import { LC_KEYS } from "../../utils/constants";
import type { CustomNote } from "./AITemplateModal/types";
import styles from "./createnotemodal.module.scss";

const SOAP_NOTE_SLUG = "soap-note";

// Hardcoded: SOL-specific NoteType ids that were removed from the picker. Existing notes/LC may
// still reference these — coerce the Select's value to undefined so it renders the placeholder
// instead of showing the raw enum number.
const REMOVED_SOL_NOTE_TYPES = new Set<number>([
  NoteType.SOL_PSYCH_INTAKE,
  NoteType.PSYCHIATRIC_FOLLOW_UP,
  NoteType.THERAPY_INTAKE,
  NoteType.THERAPY_FOLLOW_UP_SOAP,
  NoteType.THERAPY_FOLLOW_UP_DAP,
]);

/** Returns undefined for removed SOL note types so the Select shows its placeholder. Falls back to
 * the original value on any unexpected error to avoid breaking the picker. */
const getDisplayedNoteType = (noteType: number | string | undefined): number | string | undefined => {
  try {
    const asNumber = Number(noteType);
    if (Number.isFinite(asNumber) && REMOVED_SOL_NOTE_TYPES.has(asNumber)) {
      return undefined;
    }
    return noteType;
  } catch (error) {
    console.error("Failed to resolve displayed note type:", error);
    return noteType;
  }
};

const NoteTypeSelector = ({ noteTypes }: { noteTypes: { name: string; type: string }[] }) => {
  const componentName = "createnotemodal";
  const dispatch = useAppDispatch();
  const isMobile = useIsMobileView();
  const { recordingDetail } = useAppSelector((state) => state.recordings);
  const [customNoteList, setCustomNoteList] = React.useState<CustomNote[]>([]);
  const { templateOpenedFromNoteModal, templates } = useAppSelector((state) => state.appState);
  const { isMobilePath } = useAppSelector((state) => state.appState);
  const userInfo = useAppSelector((state) => state.auth.userInfo);

  const fetchCustomNoteList = async () => {
    const response = await getCustomNoteList();
    let data = await response.data;
    data = data.filter((item: CustomNote) => item.status === 0);
    setCustomNoteList(data as CustomNote[]);
  };

  const fetchAllTemplates = async () => {
    getTemplates()
      .then((response) => {
        const sortedTemplates = [...response].sort((a, b) => {
          // Put soap-note template first
          if (a.slug === SOAP_NOTE_SLUG) return -1;
          if (b.slug === SOAP_NOTE_SLUG) return 1;
          // Sort the rest alphabetically by name
          return a.name.localeCompare(b.name);
        });
        dispatch(setAllTemplates(sortedTemplates));
      })
      .catch((error) => {
        console.error("Error fetching templates:", error);
      });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: need to fetch custom note list when modal is opened
  React.useEffect(() => {
    fetchCustomNoteList();
    fetchAllTemplates();
  }, [templateOpenedFromNoteModal]);

  const allNoteTypes = [
    ...(customNoteList.length > 0
      ? [
          {
            label: "My Templates",
            title: "My Templates",
            options: customNoteList.map((note: CustomNote) => {
              if (note.is_group_accessible) {
                return {
                  label: (
                    <>
                      {note.name}
                      <span style={{ fontStyle: "italic", fontSize: "0.9em", marginLeft: "4px" }}>
                        - shared by group admin
                      </span>
                    </>
                  ),
                  value: note.id,
                  // Fuse can't search a JSX label — searchText carries the plain name so
                  // admin-shared templates show up in the dropdown search like everything else.
                  searchText: note.name,
                };
              }
              return {
                label: note.name,
                value: note.id,
                searchText: note.name,
              };
            }),
          },
        ]
      : []),
    // Nudge's stock note types are for self-serve users. An RPA practice writes
    // into its EHR through templates its implementation team built against that
    // EHR's fields, so a stock SOAP/DAP produces a note the transfer can't
    // place — same reason Medicine was already excluded here.
    ...(userInfo?.practice_area === PRACTICE_AREA.BEHAVIORAL_HEALTH && !userInfo?.group_rpa_enabled
      ? [
          {
            label: "Nudge Templates",
            title: "Nudge Templates",
            options: noteTypes.map((note: any) => {
              const noteTypeValue = Number.parseInt(NoteType[note.type]);
              return {
                label: note.name,
                value: noteTypeValue,
                searchText: note.name,
              };
            }),
          },
        ]
      : []),
    ...(templates.length > 0
      ? [
          {
            label: "Template Gallery",
            title: "Template Gallery",
            options: templates.map((note: any) => ({
              label: note.name,
              value: note.slug, // Already a string, ensure consistency
              id: note.id,
              is_template: true,
              content: note.content,
              description: note.description,
              tags: note.tags,
              searchText: note.name,
            })),
          },
        ]
      : []),
  ];
  const { search: fuseSearch } = useFuseSearch({
    data: allNoteTypes.flatMap((group) => group.options),
    // searchText, not label: admin-shared labels are JSX, which Fuse silently can't match.
    keys: ["searchText"],
  });

  function handleAddTemplate() {
    if (isMobile) {
      alert("This feature is available on Desktop");
    } else if (isMobilePath) {
      dispatch(setShowRestrictionModal(true));
    } else {
      dispatch(setAITemplateModalState(true));
      dispatch(setTemplateOpenedFromNoteModal(true));
    }
  }

  return (
    <Col span={24} className={`${styles[`${componentName}__modal-row`]}`}>
      <Select
        value={getDisplayedNoteType(recordingDetail?.note_type ?? undefined)}
        listHeight={isMobile ? 160 : 256}
        onChange={(value, option: any) => {
          if (!option.is_template) {
            localStorage.setItem(LC_KEYS.NOTE_TYPE, value.toString());
            localStorage.setItem(LC_KEYS.IS_TEMPLATE, "false");
          } else {
            localStorage.removeItem(LC_KEYS.NOTE_TYPE);
            localStorage.removeItem(LC_KEYS.IS_TEMPLATE);
          }
          dispatch(
            setRecordingDetail({
              ...recordingDetail,
              note_type: value,
              template_id: option?.id,
              is_template: option?.is_template,
              modality_type: value === NoteType.CONSULT_NOTE ? "write" : recordingDetail.modality_type, // Compare string with string representation of enum
            }),
          );
        }}
        showSearch
        placeholder="Select a document type"
        optionFilterProp="children"
        // Use Fuse.js for fuzzy search in dropdown
        filterOption={(input, option: any) => {
          // Use any for option
          if (!input) return true;
          const results = fuseSearch(input);
          return results.some((item: any) => item.value === option.value); // Use any for item
        }}
        dropdownRender={(menu) => (
          <div className="client">
            {menu}
            <Divider style={{ margin: "4px 0" }} />
            <Flex gap={10}>
              <Button type="text" icon={<PlusOutlined />} onClick={handleAddTemplate}>
                Add My Template
              </Button>
            </Flex>
          </div>
        )}
        className={`${styles[`${componentName}__modal-row-field`]}`}
        options={allNoteTypes}
      />
    </Col>
  );
};

export default NoteTypeSelector;
